import { useState, useEffect, useCallback, useMemo } from "react";
import { BranchService } from "@/services/Branch/BranchSevice";
import { SiteService } from "@/services/Site/SiteServive";
import type { branch } from "@/interface/branch.interface";
import type { site } from "@/interface/site.interface";
import type { EmployeeCategory } from "@/interface/employeeOnBoard";
// import type { IPageInfo } from "@/interface/common.interface";
import { ThrowError } from "@/api/callApi";
import {
  // createInitialPageInfo,
  getInitialSalaryCycleDates,
} from "./createSalary.constants";
// import { resetPage } from "./createSalary.utils";

export interface MasterDataState {
  // Branches
  branches: branch[];
  selectedBranchId: string;
  isLoadingBranches: boolean;
  // Sites
  availableSites: site[];
  selectedSiteId: string;
  isLoadingSites: boolean;
  // Category & tab
  selectedCategory: EmployeeCategory;
  activeTab: string;
  // Search
  searchValue: string;
  // Date range
  attendanceStartDate: string;
  attendanceEndDate: string;
}

export interface MasterDataActions {
  handleBranchSelect: (branchId: string) => void;
  handleSiteSelect: (siteId: string) => void;
  handleCategoryChange: (category: EmployeeCategory) => void;
  handleTabChange: (tab: string) => void;
  handleSearchChange: (query: string) => void;
  handleAttendanceDateRangeChange: (start: string, end: string) => void;
}

export interface MasterDataResetters {
  resetAllPagination: () => void;
}

interface PaginationResetCallback {
  (): void;
}

export const useMasterData = (onPaginationReset?: PaginationResetCallback) => {
  // Branches and sites state
  const [branches, setBranches] = useState<branch[]>([]);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [isLoadingBranches, setIsLoadingBranches] = useState<boolean>(false);

  const [allSites, setAllSites] = useState<site[]>([]);
  const [selectedSiteId, setSelectedSiteId] = useState<string>("");
  const [isLoadingSites, setIsLoadingSites] = useState<boolean>(false);

  // Category and active tab
  const [selectedCategory, setSelectedCategory] =
    useState<EmployeeCategory>("Monthly-Salary");
  const [activeTab, setActiveTab] = useState<string>("employee");

  // Search
  const [searchValue, setSearchValue] = useState<string>("");

  // Attendance date range
  const initialDates = useMemo(() => getInitialSalaryCycleDates(), []);
  const [attendanceStartDate, setAttendanceStartDate] = useState<string>(
    initialDates.startDate,
  );
  const [attendanceEndDate, setAttendanceEndDate] = useState<string>(
    initialDates.endDate,
  );

  // Combined initial load for branches & sites
  const initMasterData = useCallback(async () => {
    setIsLoadingBranches(true);
    setIsLoadingSites(true);
    try {
      const [branchRes, siteRes] = await Promise.all([
        BranchService.getBranch("pageSize=100"),
        SiteService.getSite("pageSize=200"),
      ]);

      let firstBranchId = "";
      let firstSiteId = "";
      let bList: branch[] = [];
      let sList: site[] = [];

      if (branchRes?.status && branchRes?.data?.branch) {
        bList = branchRes?.data?.branch;
        if (bList.length > 0) {
          firstBranchId = bList[0]?.id || "";
        }
      }

      if (siteRes?.status && siteRes?.data?.site) {
        sList = siteRes?.data?.site;
        if (firstBranchId) {
          const matched = sList.find(
            (s) =>
              s?.branchId === firstBranchId ||
              (s?.branch && s?.branch?.id === firstBranchId),
          );
          if (matched) {
            firstSiteId = matched.id || "";
          } else if (sList.length > 0) {
            firstSiteId = sList[0]?.id || "";
          }
        } else if (sList.length > 0) {
          firstSiteId = sList[0].id || "";
        }
      }

      setBranches(bList);
      setAllSites(sList);
      if (firstBranchId) setSelectedBranchId(firstBranchId);
      if (firstSiteId) setSelectedSiteId(firstSiteId);
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoadingBranches(false);
      setIsLoadingSites(false);
    }
  }, []);

  // Filtered sites for selected branch
  const availableSites = useMemo(() => {
    if (!selectedBranchId) return allSites;
    return allSites.filter(
      (s) =>
        s.branchId === selectedBranchId ||
        (s.branch && s.branch.id === selectedBranchId),
    );
  }, [allSites, selectedBranchId]);

  // Auto-select first site when branch changes
  useEffect(() => {
    if (availableSites.length > 0) {
      const exists = availableSites.some((s) => s.id === selectedSiteId);
      if (!exists) {
        setSelectedSiteId(availableSites[0].id || "");
      }
    } else {
      setSelectedSiteId("");
    }
  }, [availableSites, selectedSiteId]);

  // Initial load
  useEffect(() => {
    initMasterData();
  }, [initMasterData]);

  // --- Handlers ---

  const handleBranchSelect = useCallback(
    (branchId: string) => {
      setSelectedBranchId(branchId);
      onPaginationReset?.();
    },
    [onPaginationReset],
  );

  const handleSiteSelect = useCallback(
    (siteId: string) => {
      setSelectedSiteId(siteId);
      onPaginationReset?.();
    },
    [onPaginationReset],
  );

  const handleCategoryChange = useCallback(
    (category: EmployeeCategory) => {
      setSelectedCategory(category);
      onPaginationReset?.();
    },
    [onPaginationReset],
  );

  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchValue(query);
  }, []);

  const handleAttendanceDateRangeChange = useCallback(
    (start: string, end: string) => {
      setAttendanceStartDate(start);
      setAttendanceEndDate(end);
    },
    [],
  );

  return {
    // State
    branches,
    selectedBranchId,
    isLoadingBranches,
    availableSites,
    selectedSiteId,
    isLoadingSites,
    selectedCategory,
    activeTab,
    searchValue,
    attendanceStartDate,
    attendanceEndDate,

    // Handlers
    handleBranchSelect,
    handleSiteSelect,
    handleCategoryChange,
    handleTabChange,
    handleSearchChange,
    handleAttendanceDateRangeChange,
  };
};

export type UseMasterDataReturn = ReturnType<typeof useMasterData>;
